import React, { useEffect, useState } from "react";
import Box from "@mui/material/Box";
import {
  gridClasses,
  GridColDef,
  GridRenderCellParams,
  gridPaginationModelSelector,
  useGridApiContext,
  useGridSelector,
} from "@mui/x-data-grid";
import MedicationIcon from "@mui/icons-material/Medication";
import AcUnitIcon from "@mui/icons-material/AcUnit";
import FirstPageRoundedIcon from "@mui/icons-material/FirstPageRounded";
import LastPageRoundedIcon from "@mui/icons-material/LastPageRounded";
import KeyboardArrowLeftRoundedIcon from "@mui/icons-material/KeyboardArrowLeftRounded";
import KeyboardArrowRightRoundedIcon from "@mui/icons-material/KeyboardArrowRightRounded";
import Typography from "@mui/material/Typography";
import { Button, IconButton } from "@mui/material";
import moment from "moment/moment";
import axios from "axios";
import { formatPrice, getPackTypeDisplay, getSealDetailsDisplay } from "../../components/utils/utils";
import BuyAction from "./BuyAction";

export const PRODUCT_LIST_PAGE_SIZE = 10;

const INK = "#1E293B";
const INK_SOFT = "#475569";
const LINE_SOFT = "#F1F5F9";
const LINE = "#E2E8F0";
const PRIMARY = "#2563EB";
const PRIMARY_DARK = "#1D4ED8";
const BODY_FONT = '"Source Sans 3", sans-serif';
const MONO_FONT = '"Spline Sans Mono", monospace';

const pageButtonSx = (selected: boolean) => ({
  minWidth: 32,
  width: 32,
  height: 32,
  borderRadius: "8px",
  fontWeight: 600,
  fontFamily: BODY_FONT,
  p: 0,
  lineHeight: 1,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  ...(selected
    ? {
        backgroundColor: PRIMARY,
        color: "#FFFFFF",
        boxShadow: "none",
        "&:hover": { backgroundColor: PRIMARY_DARK, boxShadow: "none" },
      }
    : {
        color: INK_SOFT,
        borderColor: "#E2E8F0",
        "&:hover": { borderColor: INK_SOFT, backgroundColor: LINE_SOFT },
      }),
});

export const BuyGridPagination = (props: any) => {
  const apiRef = useGridApiContext();
  const paginationModel = useGridSelector(apiRef, gridPaginationModelSelector);
  const totalPages = Math.max(Math.ceil((props.rowCount ?? 0) / (paginationModel.pageSize || PRODUCT_LIST_PAGE_SIZE)), 1);
  const currentPage = paginationModel.page + 1;
  const windowStart = Math.max(1, Math.min(currentPage - 1, totalPages - 2));
  const windowEnd = Math.min(totalPages, windowStart + 2);
  const visiblePages = Array.from({ length: windowEnd - windowStart + 1 }, (_, index) => windowStart + index);
  const shouldShowLastPage = totalPages > 3 && windowEnd < totalPages;
  const isFirstPage = currentPage <= 1;
  const isLastPage = currentPage >= totalPages;

  return (
    <Box
      sx={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        width: "100%",
        px: 2,
        py: 0.5,
      }}
    >
      <Typography sx={{ fontFamily: MONO_FONT, fontSize: "11px", color: INK_SOFT }}>
        Page {currentPage} of {totalPages}
      </Typography>
      <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
        <IconButton size="small" sx={{ color: INK_SOFT }} onClick={() => apiRef.current.setPage(0)} disabled={isFirstPage}>
          <FirstPageRoundedIcon fontSize="small" />
        </IconButton>
        <IconButton size="small" sx={{ color: INK_SOFT }} onClick={() => apiRef.current.setPage(currentPage - 2)} disabled={isFirstPage}>
          <KeyboardArrowLeftRoundedIcon fontSize="small" />
        </IconButton>

        {visiblePages.map((page) => {
          const isSelected = currentPage === page;
          return (
            <Button
              key={page}
              size="small"
              variant={isSelected ? "contained" : "outlined"}
              onClick={() => apiRef.current.setPage(page - 1)}
              sx={pageButtonSx(isSelected)}
            >
              {page}
            </Button>
          );
        })}
        {shouldShowLastPage && (
          <>
            <Typography sx={{ px: 0.5, color: INK_SOFT, fontSize: 13 }}>
              ...
            </Typography>
            <Button
              size="small"
              variant={currentPage === totalPages ? "contained" : "outlined"}
              onClick={() => apiRef.current.setPage(totalPages - 1)}
              sx={pageButtonSx(currentPage === totalPages)}
            >
              {totalPages}
            </Button>
          </>
        )}

        <IconButton size="small" sx={{ color: INK_SOFT }} onClick={() => apiRef.current.setPage(currentPage)} disabled={isLastPage}>
          <KeyboardArrowRightRoundedIcon fontSize="small" />
        </IconButton>
        <IconButton size="small" sx={{ color: INK_SOFT }} onClick={() => apiRef.current.setPage(totalPages - 1)} disabled={isLastPage}>
          <LastPageRoundedIcon fontSize="small" />
        </IconButton>
      </Box>
    </Box>
  );
};

export const DrugCellRenderer = ({ params }: { params: GridRenderCellParams<any> }) => {
  const [imageUrl, setImageUrl] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const drugNdc = params?.row?.ndc;

  useEffect(() => {
    if (!drugNdc) return;
    const formattedNdc = drugNdc.replace(/-/g, "");
    setIsLoading(true);
    const imageUrlApi = `${process.env.REACT_APP_API_URL}/drug-images?ndc=${formattedNdc}`;
    axios
      .get(imageUrlApi)
      .then((response) => {
        setImageUrl(response.data.imageUrl);
      })
      .catch((error) => {
        console.error("Error fetching drug image:", error);
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, [drugNdc]);

  const thumbSx = {
    width: 36,
    height: 36,
    borderRadius: "7px",
    marginRight: "8px",
    flexShrink: 0,
    overflow: "hidden",
  };

  const secondaryLine = [params.row.ndc, params.row.strength].filter(Boolean).join(" · ");

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        minWidth: 0,
        width: "100%",
        height: "100%",
        py: 0,
      }}
    >
      {isLoading ? (
        <Box sx={{ ...thumbSx, bgcolor: LINE_SOFT }} />
      ) : imageUrl ? (
        <Box
          component="img"
          src={imageUrl}
          alt="Drug"
          sx={{ ...thumbSx, border: `1px solid ${LINE_SOFT}`, objectFit: "cover", display: "block" }}
        />
      ) : (
        <Box
          sx={{
            ...thumbSx,
            bgcolor: "#F1F5F9",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <MedicationIcon sx={{ fontSize: 20, color: PRIMARY }} />
        </Box>
      )}
      <Box sx={{ minWidth: 0, flex: 1 }}>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: "4px",
            minWidth: 0,
            lineHeight: 1.15,
          }}
        >
          <Box
            sx={{
              fontWeight: 600,
              fontSize: "13px",
              color: INK,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {params.value}
          </Box>
          {params.row.isFrozen && (
            <AcUnitIcon
              titleAccess="Refrigerated"
              sx={{ fontSize: 14, color: "#4A90D9", flexShrink: 0 }}
            />
          )}
        </Box>
        <Box
          sx={{
            fontFamily: MONO_FONT,
            fontSize: "10px",
            color: INK_SOFT,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            lineHeight: 1.15,
          }}
        >
          {secondaryLine}
        </Box>
      </Box>
    </Box>
  );
};

export function mapProductsToGridRows(products: any[] | undefined | null) {
  return (
    products?.map((product: any) => ({
      ...product,
      expirationDate: moment(product.expDate),
      available: product.quantity,
      priceUnit: Number(product.unitPrice),
      price: Number(product.price),
      packaging: product.quantityPerPack,
      packagingValue: product.packaging,
      packType: product.type,
      sealed: product.packageCondition,
      sealDetails: product.sealDetails,
    })) ?? []
  );
}

const SealTag = ({ sealed }: { sealed: boolean }) => {
  const style = sealed
    ? { color: "#2E7D32", bg: "#E8F5E9", dot: "#66BB6A" }
    : { color: "#B45309", bg: "#FEF3C7", dot: "#F59E0B" };

  return (
    <Box
      sx={{
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        gap: "4px",
        height: 16,
        px: "5px",
        boxSizing: "border-box",
        borderRadius: "4px",
        fontFamily: MONO_FONT,
        fontSize: "9px",
        fontWeight: 600,
        letterSpacing: "0.04em",
        textTransform: "uppercase",
        whiteSpace: "nowrap",
        lineHeight: 1,
        color: style.color,
        backgroundColor: style.bg,
      }}
    >
      <Box
        sx={{
          width: 4,
          height: 4,
          borderRadius: "50%",
          bgcolor: style.dot,
          flexShrink: 0,
        }}
      />
      {sealed ? "Sealed" : "Non-Sealed"}
    </Box>
  );
};

export function getProductListGridColumns(options: {
  currentUserId: number;
  updateCartLoading: boolean;
  handleCart: (params: any, packageAvailable: number) => void | Promise<void>;
  onSeeDetails: (params: any) => void;
  pinDrugColumn?: boolean;
}): GridColDef[] {
  const { currentUserId, updateCartLoading, handleCart, onSeeDetails, pinDrugColumn = true } = options;

  const seeDetailsButton = (params: any) => (
    <Button
      size="small"
      variant="text"
      onClick={(event) => {
        event.stopPropagation();
        onSeeDetails(params);
      }}
      sx={{
        height: 24,
        minWidth: 0,
        px: 1,
        color: INK_SOFT,
        fontFamily: BODY_FONT,
        fontWeight: 600,
        fontSize: 12,
        textTransform: "none",
        whiteSpace: "nowrap",
        flexShrink: 0,
        "&:hover": {
          color: PRIMARY,
          bgcolor: "transparent",
          textDecoration: "underline",
          textUnderlineOffset: "2px",
        },
      }}
    >
      Details
    </Button>
  );

  return [
    {
      field: "drugName",
      headerName: "Drug name",
      editable: false,
      disableColumnMenu: true,
      sortable: true,
      disableExport: true,
      disableReorder: true,
      hideable: false,
      flex: 2.2,
      minWidth: 220,
      ...(pinDrugColumn
        ? { cellClassName: "stickyCol", headerClassName: "stickyCell" }
        : {}),
      renderCell: (params: GridRenderCellParams<any>) => <DrugCellRenderer params={params} />,
    },
    {
      field: "packaging",
      headerName: "Pack size",
      editable: false,
      disableColumnMenu: true,
      sortable: true,
      flex: 0.6,
      minWidth: 80,
      cellClassName: "monoCell",
    },
    {
      field: "packType",
      headerName: "Type",
      editable: false,
      disableColumnMenu: true,
      sortable: true,
      flex: 0.8,
      minWidth: 85,
      renderCell: (params: any) => getPackTypeDisplay(params.value, params.row.packagingValue),
    },
    {
      field: "sealed",
      headerName: "Sealed",
      editable: false,
      disableColumnMenu: true,
      sortable: true,
      flex: 1.1,
      minWidth: 120,
      renderCell: (params: any) => (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "center",
            minWidth: 0,
            width: "100%",
            height: "100%",
            gap: 0.25,
          }}
        >
          <SealTag sealed={params.row.packageCondition === 1} />
          {params.row.sealDetails && (
            <Box
              sx={{
                fontSize: "10px",
                color: INK_SOFT,
                fontFamily: BODY_FONT,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                maxWidth: 100,
                lineHeight: 1.15,
                textAlign: "center",
              }}
            >
              {getSealDetailsDisplay(params.row.sealDetails)}
            </Box>
          )}
        </Box>
      ),
    },
    {
      field: "expirationDate",
      headerName: "Expires",
      editable: false,
      disableColumnMenu: true,
      sortable: true,
      flex: 0.7,
      minWidth: 82,
      type: "dateTime",
      valueFormatter: (value) => moment(value).format("MM/YYYY"),
      cellClassName: "monoCell",
    },
    {
      field: "price",
      headerName: "Price",
      editable: false,
      disableColumnMenu: true,
      sortable: true,
      flex: 0.8,
      minWidth: 85,
      type: "number",
      align: "left",
      headerAlign: "left",
      cellClassName: "monoStrongCell",
      renderCell: (params: any) => `$${formatPrice(params.value)}`,
    },
    {
      field: "priceUnit",
      headerName: "Unit price",
      editable: false,
      disableColumnMenu: true,
      sortable: true,
      flex: 0.7,
      minWidth: 90,
      type: "number",
      align: "left",
      headerAlign: "left",
      cellClassName: "monoCell",
      renderCell: (params: any) => `$${formatPrice(params.value)}`,
    },
    {
      field: "available",
      headerName: "Qty",
      editable: false,
      disableColumnMenu: true,
      sortable: true,
      flex: 0.5,
      minWidth: 60,
      cellClassName: "monoCell",
    },
    {
      field: "action",
      headerName: "",
      editable: false,
      sortable: false,
      disableColumnMenu: true,
      flex: 1.8,
      minWidth: 280,
      renderCell: (params: any) => {
        if (params.row.available < 1) {
          return (
            <Box sx={{ flex: 1, display: "flex", justifyContent: "end", alignItems: "center", pr: 1 }}>
              {seeDetailsButton(params)}
            </Box>
          );
        }

        if (params.row.userId === currentUserId) {
          return (
            <Box sx={{ flex: 1, display: "flex", justifyContent: "end", alignItems: "center", pr: 1, gap: 0.75, flexWrap: "nowrap" }}>
              {seeDetailsButton(params)}
              <Box
                sx={{
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  height: 22,
                  px: "8px",
                  boxSizing: "border-box",
                  borderRadius: "5px",
                  fontFamily: MONO_FONT,
                  fontSize: "10.5px",
                  fontWeight: 600,
                  letterSpacing: "0.05em",
                  textTransform: "uppercase",
                  color: INK_SOFT,
                  backgroundColor: LINE_SOFT,
                  whiteSpace: "nowrap",
                  lineHeight: 1,
                  flexShrink: 0,
                }}
              >
                My listing
              </Box>
            </Box>
          );
        }

        return (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "flex-end",
              width: "100%",
              height: "100%",
              gap: 0.75,
              pr: 0.5,
              flexWrap: "nowrap",
              overflow: "visible",
            }}
          >
            <BuyAction
              rowParams={params}
              updateCartLoading={updateCartLoading}
              handleAvailableCart={(packageAvailable) => handleCart(params, packageAvailable)}
              useAddToCartButton
            />
            {seeDetailsButton(params)}
          </Box>
        );
      },
    },
  ];
}

export const productListDataGridSx = {
  border: "none",
  fontFamily: BODY_FONT,
  fontSize: "12.5px",
  color: INK,
  [`& .${gridClasses.cell}:focus, & .${gridClasses.cell}:focus-within`]: {
    outline: "none",
  },
  [`& .${gridClasses.columnHeader}:focus, & .${gridClasses.columnHeader}:focus-within`]: {
    outline: "none",
  },
  "& .MuiDataGrid-columnHeaders": {
    backgroundColor: "#F1F5F9",
    borderBottom: `1px solid ${LINE}`,
    minHeight: "40px !important",
    maxHeight: "40px !important",
  },
  "& .MuiDataGrid-columnHeaderTitle": {
    fontFamily: MONO_FONT,
    fontSize: "10px",
    fontWeight: 600,
    letterSpacing: "0.05em",
    textTransform: "uppercase",
    color: INK,
  },
  "& .MuiDataGrid-cell": {
    display: "flex",
    alignItems: "center",
    borderBottom: `1px solid ${LINE_SOFT}`,
    overflow: "hidden",
    py: 0,
  },
  "& .MuiDataGrid-cellContent": {
    display: "flex",
    alignItems: "center",
    width: "100%",
    height: "100%",
    overflow: "hidden",
  },
  "& .MuiDataGrid-row": {
    transition: "background-color .15s ease",
  },
  [`& .${gridClasses.row}:hover`]: {
    cursor: "pointer",
    backgroundColor: "#F1F5F9",
  },
  [`& .${gridClasses.row}:hover .stickyCol, & .${gridClasses.row}.Mui-hovered .stickyCol`]: {
    backgroundColor: "#F1F5F9",
  },
  "& .monoCell": {
    fontFamily: MONO_FONT,
    fontSize: "11.5px",
    color: INK_SOFT,
  },
  "& .monoStrongCell": {
    fontFamily: MONO_FONT,
    fontSize: "11.5px",
    fontWeight: 600,
    color: INK,
  },
  "& .MuiDataGrid-footerContainer": {
    minHeight: 44,
    borderTop: `1px solid ${LINE}`,
    backgroundColor: "#FFFFFF",
  },
};

export function attachStickyColumnScrollListener() {
  const handleScrollHorizontal = () => {
    const currentScrollPos = document.querySelector(".MuiDataGrid-virtualScroller")?.scrollLeft || 0;
    const firstColumnHeader = document.querySelector<HTMLElement>(".stickyCell");
    if (firstColumnHeader) {
      firstColumnHeader.style.transform = `translate3d(${currentScrollPos}px, 0px, 0px)`;
    }
  };

  let timer: ReturnType<typeof setTimeout> | undefined;
  const findVirtualScroller = () => {
    const virtualScrollerElement = document.querySelector(".MuiDataGrid-virtualScroller");
    if (!virtualScrollerElement) {
      timer = setTimeout(findVirtualScroller, 100);
    } else {
      virtualScrollerElement.addEventListener("scroll", handleScrollHorizontal);
    }
  };
  findVirtualScroller();

  return () => {
    if (timer) clearTimeout(timer);
    const virtualScrollerElement = document.querySelector(".MuiDataGrid-virtualScroller");
    virtualScrollerElement?.removeEventListener("scroll", handleScrollHorizontal);
  };
}
